import { useEffect } from 'react';
import { RouterProvider } from 'react-router';
import { isConfigured } from '../lib/env.ts';
import { applyBrandColor } from '../lib/theme.ts';
import { setMonitoringContext } from '../lib/monitoring.ts';
import { setSyncContext } from '../services/sync.ts';
import { setLicenseBusiness } from '../services/license.ts';
import { authLinkOnLoad } from '../lib/supabase.ts';
import { FullScreenMessage, Spinner } from '../ui/index.tsx';
import { SetupPage } from '../features/setup/SetupPage.tsx';
import { LockScreen } from '../features/auth/LockScreen.tsx';
import { DeviceRevokedScreen } from '../features/auth/DeviceRevokedScreen.tsx';
import { SetPasswordPage } from '../features/auth/SetPasswordPage.tsx';
import { UpdatePrompt } from '../features/pwa/UpdatePrompt.tsx';
import { SessionProvider, useCurrentState } from './current.tsx';
import { rememberActiveBusiness } from '../lib/db.ts';
import { router } from './router.tsx';

/**
 * Puerta de entrada. Decide qué mostrar según el estado del dispositivo:
 * sin configurar → alta del dispositivo · deshabilitado → aviso · sin sesión → elegir usuario · con sesión → la app.
 */
export function App() {
  const current = useCurrentState();

  const business = current?.business;
  const session = current?.session;
  const membership = current?.membership;
  const activeSession = session && membership?.active ? session : undefined;

  useEffect(() => {
    void setLicenseBusiness(current?.device?.business_id ?? null);
  }, [current?.device?.business_id]);

  // Lista de negocios de este dispositivo (para «Cambiar de negocio»).
  useEffect(() => {
    if (business) rememberActiveBusiness(business.id, business.name, business.logo_path);
  }, [business?.id, business?.name, business?.logo_path]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    applyBrandColor(business?.theme?.primary);
  }, [business?.theme?.primary]);

  useEffect(() => {
    setSyncContext({ businessId: activeSession?.business_id ?? null, userId: activeSession?.user_id ?? null });
    setMonitoringContext({
      businessId: current?.device?.business_id,
      deviceId: current?.device?.id,
      role: membership?.role,
    });
  }, [activeSession?.business_id, activeSession?.user_id, current?.device?.business_id, current?.device?.id, membership?.role]);

  if (!isConfigured) {
    return (
      <FullScreenMessage title="Falta configurar la conexión">
        <p>
          Copiá <code>apps/pos/.env.example</code> como <code>apps/pos/.env.local</code> y completá la URL y la clave
          pública de Supabase. Después reiniciá <code>npm run dev</code>.
        </p>
      </FullScreenMessage>
    );
  }

  let content;
  if (location.pathname === '/definir-clave' || authLinkOnLoad) {
    content = <SetPasswordPage />;
  } else if (!current) {
    content = (
      <div className="flex min-h-full items-center justify-center text-slate-500">
        <Spinner />
      </div>
    );
  } else if (!current.device || !current.business) {
    content = <SetupPage pendingDevice={current.device} />;
  } else if (current.deviceRow?.revoked_at) {
    content = <DeviceRevokedScreen />;
  } else if (!activeSession || !membership) {
    content = <LockScreen device={current.device} business={current.business} deactivated={!!session && !membership?.active} />;
  } else {
    content = (
      <SessionProvider
        value={{ device: current.device, session: activeSession, business: current.business, membership }}
      >
        <RouterProvider router={router} />
      </SessionProvider>
    );
  }

  return (
    <>
      {content}
      <UpdatePrompt />
    </>
  );
}
